import {
  LivefireCardType,
  click,
  comment,
  like,
  open,
  play,
  replyWithVideo,
  sent,
  videoWatched,
  EMAIL_TYPES,
} from '@/constants/LivefireItemTypes';

export type LivefireApiOptions = {
  cardTypes?: LivefireCardType[];
  clientId?: string | null;
  token?: string | null;
  feeds?: string[];
};

export type LivefireSocketEventDetail = {
  clientId: string | null;
  error: unknown | null;
  reason?: unknown | null;
  socket: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  token: string | null;
  url: string | null | URL;
};

/*
  Livefire has two similar data structures: FeedItems and LivefireItems.

  FeedItems are what LF uses to display data in its UI. LivefireItems are
  what Livefire receives from the API/sockets when events occur. These are
  then converted into a FeedItem for display.
*/

/** Create a union type here of each type available */
export type FeedItem =
  | EmailFeedItem
  | CommentFeedItem
  | ReplyWithVideoFeedItem
  | VideoWatchedFeedItem
  | LikeFeedItem;

type BaseFeedItem<T = unknown> = {
  eventTS: number;
  id: string;
  muted: boolean;
  recipientName: string;
  // in practice, this can have an undefined value somehow, so including that
  // here so we can defend against it...
  recipients?: Recipient[];
  subject: string;
  sentTS: number;
  type: LivefireCardType;
  updateTimerTick?: number;
  _original: T;
};

type EmailType = (typeof EMAIL_TYPES)[number];
export function isEmailFeedItem(feedItem: FeedItem): feedItem is EmailFeedItem {
  return EMAIL_TYPES.includes(feedItem.type as EmailType);
}

/** A FeedItem for email related events */
export type EmailFeedItem = Omit<BaseFeedItem<EmailLivefireItem>, 'count'> & {
  count?: number;
  type: (typeof EMAIL_TYPES)[number];
};

export function isCommentFeedItem(feedItem: FeedItem): feedItem is CommentFeedItem {
  return feedItem.type === comment;
}

export function isLikeFeedItem(feedItem: FeedItem): feedItem is LikeFeedItem {
  return feedItem.type === like;
}

/** A FeedItem for comment-on-video related events */
export type CommentFeedItem = Omit<BaseFeedItem<CommentLivefireItem>, 'recipients'> & {
  count: number;
  commentMessage: string;
  videoId: string;
  type: typeof comment;
};

export function isReplyWithVideoFeedItem(feedItem: FeedItem): feedItem is ReplyWithVideoFeedItem {
  return feedItem.type === replyWithVideo;
}

/** A FeedItem for reply with video related events */
export type ReplyWithVideoFeedItem = Omit<
  BaseFeedItem<ReplyWithVideoLivefireItem>,
  'recipients' | 'count'
> & {
  replyVideoId: string;
  replyVideoName: string;
  replyVideoUrl: string;
  replyThumbnail: string;
  commentMessage: string;
  videoId: string;
  type: typeof replyWithVideo;
  _original: ReplyWithVideoLivefireItem;
};

export function isVideoWatchedFeedItem(feedItem: FeedItem): feedItem is VideoWatchedFeedItem {
  return feedItem.type === videoWatched;
}

export type VideoWatchedFeedItem = BaseFeedItem<VideoWatchedLivefireItem> & {
  count: number;
  videoId: string;
  percentWatched: number;
  maxVideoWatchedPercent: number;
  type: typeof videoWatched;
};

export function isLikedFeedItem(feedItem: FeedItem): feedItem is LikeFeedItem {
  return feedItem.type === like;
}

export type LikeFeedItem = Omit<BaseFeedItem<LikeLivefireItem>, 'recipients'> & {
  count: number;
  videoId: string;
  type: typeof like;
};

/*
  LivefireItems are the second structure which represent data LF receives as events
  from the API. These structures are similar, but different than the actual FeedItems
  they become
*/

/** All available LivefireItems */
export type LivefireItem =
  | CommentLivefireItem
  | ReplyWithVideoLivefireItem
  | VideoWatchedLivefireItem
  | LikeLivefireItem
  | EmailLivefireItem;

type BaseLivefireItem = {
  id: string;
  updated_timestamp: number;
  interactionDate: number;
};

/** A comment-on-video event */
export type CommentLivefireItem = BaseLivefireItem & {
  totalLikes: CommentFeedItem['count'];
  muted: unknown;
  emailAddress?: string;
  videoName: CommentFeedItem['subject'];
  message: CommentFeedItem['commentMessage'];
  videoId: CommentFeedItem['videoId'];
  itemType: typeof comment;
};

/** A reply with video event event */
export type ReplyWithVideoLivefireItem = BaseLivefireItem & {
  videoName: FeedItem['subject'];
  muted: unknown;
  replierEmail: string;
  replyThumbnail: ReplyWithVideoFeedItem['replyThumbnail'];
  replyVideoId: ReplyWithVideoFeedItem['replyVideoId'];
  replyVideoName: ReplyWithVideoFeedItem['replyVideoName'];
  replyVideoUrl: ReplyWithVideoFeedItem['replyVideoUrl'];
  message: ReplyWithVideoFeedItem['commentMessage'];
  videoId: ReplyWithVideoFeedItem['videoId'];
  itemType: typeof replyWithVideo;
};

export type VideoWatchedLivefireItem = BaseLivefireItem & {
  totalViews: VideoWatchedFeedItem['count'];
  videoWatchedPercent: VideoWatchedFeedItem['percentWatched'];
  maxVideoWatchedPercent: VideoWatchedFeedItem['maxVideoWatchedPercent'];
  videoId: VideoWatchedFeedItem['videoId'];
  muted: unknown;
  emailAddress?: VideoWatchedFeedItem['recipientName'];
  recipients: VideoWatchedFeedItem['recipients'];
  viewDate: VideoWatchedFeedItem['sentTS'];
  videoName: VideoWatchedFeedItem['subject'];
  itemType: typeof videoWatched;
};

export type LikeLivefireItem = BaseLivefireItem & {
  videoName: LikeFeedItem['subject'];
  muted: unknown;
  totalLikes: LikeFeedItem['count'];
  videoId: LikeFeedItem['videoId'];
  emailAddress?: LikeFeedItem['recipientName'];
  itemType: typeof like;
};

export type EmailLivefireItem = BaseLivefireItem & {
  sendDate: EmailFeedItem['eventTS'];
  contactName?: EmailFeedItem['recipientName'];
  recipientName?: EmailFeedItem['recipientName'];
  count: number;
  videoId: string;
  muted: unknown;
  recipients: EmailFeedItem['recipients'];
  subject?: EmailFeedItem['subject'];
  opens?: EmailFeedItem['count'];
  latestOpenTimestamp?: number;
  clicks?: EmailFeedItem['count'];
  latestClickTimestamp?: number;
  plays?: EmailFeedItem['count'];
  latestPlayTimestamp?: number;
  itemType: typeof open | typeof click | typeof play | typeof sent;
};

export type LivefireApiEventDetail =
  | LivefireAuthenticationFailedEventDetail
  | LivefireConnectErrorEventDetail
  | LivefireConnectEventDetail
  | LivefireDisconnectEventDetail
  | LivefireNewItemEventDetail
  | LivefireReconnectAttemptEventDetail
  | LivefireReconnectErrorEventDetail
  | LivefireReconnectFailedEventDetail;

export type LivefireAuthenticationFailedEventDetail = LivefireSocketEventDetail & { data: unknown };
export type LivefireConnectErrorEventDetail = LivefireConnectEventDetail & { error: unknown };
export type LivefireConnectEventDetail = LivefireSocketEventDetail;
export type LivefireDisconnectEventDetail = LivefireConnectEventDetail & { reason: string };
export type LivefireNewItemEventDetail = LivefireSocketEventDetail & { item: FeedItem };
export type LivefireReconnectEventDetail = LivefireSocketEventDetail & { attempt?: number | null };
export type LivefireReconnectAttemptEventDetail = LivefireReconnectEventDetail & {
  attempt: number;
};
export type LivefireReconnectErrorEventDetail = LivefireReconnectEventDetail & { error: Error };
export type LivefireReconnectFailedEventDetail = LivefireReconnectEventDetail & {
  attempt: number;
  error: Error;
};

type Recipient = {
  name: string;
  email: string;
};

export type FeedItemsResult = {
  ok: boolean;
  feedItems: FeedItem[];
};
